import React, { useState } from 'react';
import { Badge } from '../common/Badge';
import { ConfirmButton } from '../common/ConfirmButton';
import { EntrySummaryRow } from './EntrySummaryRow';
import { getEntryCodes } from '../../lib/helpers';
import { usePhotoUrlList } from '../../hooks/usePhotoUrl';

export function EvidenceCard({ entry, unitLookup, onLightbox, onDelete, onEdit, onPDF, canDelete, canEdit, resubmitOnEdit = true, collapsible = false }) {
  const codes = getEntryCodes(entry);
  // Entries needing attention open by default — those are the ones to act on
  const [open, setOpen] = useState(!collapsible || entry.status === 'rejected');

  // Build photo list from either new photo_urls array or legacy fields
  const rawList = (() => {
    if (entry.photo_urls) {
      try { return JSON.parse(entry.photo_urls); } catch {}
    }
    return [entry.photo_fault_url, entry.photo_repair_url, entry.photo_completed_url, entry.photo_additional_url].filter(Boolean);
  })();

  const photoUrls = usePhotoUrlList(rawList);

  // Parse structured fields — check for new fields first, fall back to what_doing/why_doing
  const hasFaultReason = !!entry.fault_reason;
  const hasToolsUsed = !!entry.tools_used;
  const hasOutcome = !!entry.outcome;

  if (collapsible && !open) {
    return (
      <div className="ev-card" style={{ marginBottom: 8 }}>
        <EntrySummaryRow
          entry={entry}
          codes={codes}
          photoCount={rawList.length}
          open={false}
          onToggle={() => setOpen(true)}
        />
      </div>
    );
  }

  return (
    <div className="ev-card">
      {collapsible ? (
        <EntrySummaryRow
          entry={entry}
          codes={codes}
          photoCount={rawList.length}
          open
          onToggle={() => setOpen(false)}
        />
      ) : (
        <div className="ev-hdr">
          <div>
            <div className="ev-unit">{codes.join(', ') || entry.unit_code || '—'}</div>
            <div className="ev-unitname">
              {codes.map(c => unitLookup(c)).join(' · ')}
            </div>
            <div className="ev-meta">
              Job #{entry.job_number}
              {entry.vehicle_rego && <span> · {entry.vehicle_rego}</span>}
              {' · '}{entry.date}
            </div>
          </div>
          <Badge status={entry.status || 'pending'} />
        </div>
      )}
      <div className="ev-body">
        {collapsible && (
          <div className="ev-unitname" style={{ marginBottom: 10 }}>
            {codes.map(c => unitLookup(c)).join(' · ')}
          </div>
        )}
        {/* Rejection feedback — shown prominently so the apprentice knows what to fix */}
        {entry.status === 'rejected' && (
          <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6, background: 'rgba(192,57,43,.08)', border: '1px solid rgba(192,57,43,.35)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red)', letterSpacing: 0.4, marginBottom: 4 }}>
              ⚠ CHANGES REQUESTED BY TRAINER
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text)' }}>
              {entry.review_note
                ? entry.review_note
                : 'Your trainer has requested changes. Edit this entry and resubmit it for review.'}
            </div>
          </div>
        )}

        {/* A comment left on an approved entry — praise or a pointer for next time */}
        {entry.status === 'approved' && entry.review_note && (
          <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 6, background: 'rgba(39,174,96,.07)', border: '1px solid rgba(39,174,96,.3)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', letterSpacing: 0.4, marginBottom: 4 }}>
              ✓ TRAINER COMMENT
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text)' }}>{entry.review_note}</div>
          </div>
        )}

        {hasFaultReason ? (
          <>
            <div className="ev-field-label">Fault / Reason</div>
            <div className="ev-field-val">{entry.fault_reason}</div>
            <div className="ev-field-label">What did you do?</div>
            <div className="ev-field-val">{entry.what_doing || '—'}</div>
            {hasToolsUsed && (
              <>
                <div className="ev-field-label">Tools / Equipment</div>
                <div className="ev-field-val">{entry.tools_used}</div>
              </>
            )}
            <div className="ev-field-label">Outcome</div>
            <div className="ev-field-val">{entry.outcome || entry.why_doing || '—'}</div>
          </>
        ) : (
          <>
            <div className="ev-field-label">What are you doing?</div>
            <div className="ev-field-val">{entry.what_doing || '—'}</div>
            <div className="ev-field-label">Why are you doing it?</div>
            <div className="ev-field-val">{entry.why_doing || '—'}</div>
          </>
        )}

        {rawList.length > 0 && (
          <div className="ev-field-label" style={{ marginTop: 10 }}>
            {rawList.length} photo{rawList.length === 1 ? '' : 's'} / video{rawList.length === 1 ? '' : 's'} attached
          </div>
        )}

        {photoUrls.length > 0 && (
          <div className="ev-photos">
            {photoUrls.map((src, i) => {
              if (!src) return null;
              const isVid = /\.(mp4|mov|webm|avi|mkv)/i.test(rawList[i] || '');
              if (isVid) {
                return (
                  <div key={i} style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }} onClick={() => onLightbox(src)} title={`Video ${i + 1}`}>
                    <video src={src} className="ev-photo" muted style={{ objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.3)', borderRadius: 4 }}>
                      <span style={{ fontSize: 20, color: '#fff' }}>▶</span>
                    </div>
                  </div>
                );
              }
              return (
                <img key={i} src={src} className="ev-photo" title={`Photo ${i + 1}`} onClick={() => onLightbox(src)} alt={`Photo ${i + 1}`} />
              );
            })}
          </div>
        )}

        {/* Actions — edit for apprentices and assessing staff, delete for apprentices,
            download PDF when approved */}
        {(canEdit || (canDelete && entry.status === 'pending') || (onPDF && entry.status === 'approved')) && (
          <div className="ev-actions">
            {canEdit && onEdit && (
              entry.status === 'rejected' && resubmitOnEdit ? (
                <button
                  className="btn-sm"
                  style={{ fontSize: 11, padding: '4px 12px', background: 'var(--red)', color: '#fff', borderColor: 'var(--red)', fontWeight: 700 }}
                  onClick={() => onEdit(entry)}
                >
                  ↻ Fix &amp; Resubmit
                </button>
              ) : (
                <button className="btn-sm" style={{ fontSize: 11, padding: '4px 12px' }} onClick={() => onEdit(entry)}>
                  ✏️ Edit {entry.status === 'approved' ? 'approved entry' : entry.status === 'rejected' ? 'rejected entry' : 'entry'}
                </button>
              )
            )}
            {canDelete && entry.status === 'pending' && (
              <ConfirmButton onConfirm={() => onDelete(entry.id)} label="Delete" confirmLabel="Confirm Delete?" />
            )}
            {onPDF && entry.status === 'approved' && (
              <button className="btn-sm" style={{ fontSize: 11, padding: '4px 12px' }} onClick={() => onPDF(entry)}>
                ⬇ Download PDF
              </button>
            )}
          </div>
        )}

        {/* Info note when editing will reset approval */}
        {canEdit && resubmitOnEdit && entry.status === 'approved' && (
          <div style={{ marginTop: 8, padding: '5px 10px', borderRadius: 4, background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.2)', fontSize: 10, color: '#92400e' }}>
            Editing this entry will send it back for trainer review.
          </div>
        )}
      </div>
    </div>
  );
}
